import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Typography, Chip, Alert, AlertTitle, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Stepper, Step, StepLabel, Stack, Divider, Paper,
  Table, TableBody, TableCell, TableHead, TableRow,
  MenuItem, TextField, RadioGroup, Radio, FormControlLabel, FormControl,
  FormLabel, Tooltip, IconButton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { tenantApi } from '../../api/tenantApi';

/*
 * CSV import for STANDARD inventory items only.
 *
 * Bundles are deliberately excluded: a bundle's stock is computed from its
 * linked components, so it can't be described by a flat row, and the backend
 * rejects `type: 'bundle'` on POST /inventory/batch. See PRODUCT_BACKLOG.md
 * ("CSV Import for Bundle Items") for the follow-up.
 */

// Max rows accepted from a single file. Keeps the browser responsive and the
// number of batch requests bounded (batch endpoint caps at 100 items each).
const MAX_ROWS = 5000;
const BATCH_SIZE = 100;
// `batchUpsert` rejects request bodies over 1 MB. 100 rows carrying long
// descriptions can exceed that, so chunks are also capped by serialized size
// with headroom for multi-byte characters and the JSON envelope.
const BATCH_MAX_BYTES = 700 * 1024;
// Ceiling for the free-text fields (description, internal notes). Matches
// `validators.inventoryItem`. Not a DynamoDB constraint — an item would have to
// reach 400 KB to hit one — just a guard against unbounded input. Each of these
// is stored twice (the `*Lower` search shadow field), so the real per-item cost
// is roughly double.
const TEXT_FIELD_MAX = 4000;
// Existing items are paged in for SKU duplicate detection.
const LOOKUP_PAGE_SIZE = 200;
const LOOKUP_MAX_PAGES = 25;

const STEPS = ['Upload file', 'Map columns', 'Review', 'Import'];

const ACTION_CHIP = {
  create: { label: 'New', color: 'success' },
  update: { label: 'Update', color: 'info' },
  skip: { label: 'Skip', color: 'default' },
  error: { label: 'Error', color: 'error' },
};

// ─── Target field catalogue ──────────────────────────────────────────────────
// `aliases` drive auto-detection of a CSV header. `feature` gates the field
// behind a tenant feature flag (hidden entirely when the flag is off).

const FIELDS = [
  { key: 'name', label: 'Name', required: true, type: 'string', maxLen: 200,
    help: 'Item display name.', aliases: ['name', 'itemname', 'item', 'title', 'productname', 'product', 'description1'] },
  { key: 'sku', label: 'SKU', required: true, type: 'string', maxLen: 100,
    help: 'Unique stock keeping unit. Used to detect duplicates.', aliases: ['sku', 'itemcode', 'code', 'partnumber', 'partno', 'itemnumber', 'itemno', 'productcode'] },
  { key: 'quantity', label: 'Quantity', type: 'number', min: -999999,
    help: 'On-hand quantity. Defaults to 0 when blank.', aliases: ['quantity', 'qty', 'onhand', 'onhandqty', 'stock', 'stocklevel', 'count'] },
  { key: 'unit', label: 'Unit', type: 'string', maxLen: 50,
    help: 'Unit of measure (piece, box, kg...). Defaults to "unit".', aliases: ['unit', 'uom', 'unitofmeasure', 'measure', 'units'] },
  { key: 'category', label: 'Category', type: 'string', maxLen: 200,
    help: 'Created automatically if it does not exist. Defaults to "Uncategorized".', aliases: ['category', 'categoryname', 'group', 'productgroup', 'type', 'class'] },
  { key: 'description', label: 'Description', type: 'string', maxLen: TEXT_FIELD_MAX,
    help: `Long description, up to ${TEXT_FIELD_MAX.toLocaleString()} characters.`, aliases: ['description', 'desc', 'details', 'notes', 'longdescription'] },
  { key: 'descriptionFormat', label: 'Description Format', type: 'string', maxLen: 20,
    help: 'plain, markdown, html, or rtf. Detected automatically when the column is absent.',
    aliases: ['descriptionformat', 'descformat', 'format', 'textformat', 'bodyformat'] },
  { key: 'internalNotes', label: 'Internal Notes', type: 'string', maxLen: TEXT_FIELD_MAX,
    help: 'Private notes, not shown on reports.', aliases: ['internalnotes', 'privatenotes', 'internalnote', 'comment', 'comments'] },
  { key: 'costPrice', label: 'Cost Price', type: 'number', min: 0,
    help: 'Purchase cost per unit. Currency symbols and commas are stripped.', aliases: ['costprice', 'cost', 'unitcost', 'purchaseprice', 'buyprice', 'wholesale'] },
  { key: 'salePrice', label: 'Sale Price', type: 'number', min: 0,
    help: 'Selling price per unit.', aliases: ['saleprice', 'price', 'retailprice', 'sellprice', 'unitprice', 'msrp', 'retail'] },
  { key: 'barcode', label: 'Barcode', type: 'string', maxLen: 200,
    help: 'UPC/EAN. Left unset when blank.', aliases: ['barcode', 'upc', 'ean', 'gtin', 'scancode'] },
  { key: 'reorderPoint', label: 'Reorder Point', type: 'number', min: 0,
    help: 'Low-stock threshold that triggers alerts.', aliases: ['reorderpoint', 'reorderlevel', 'minqty', 'minimum', 'minstock', 'lowstockthreshold', 'threshold'] },
  { key: 'reorderQty', label: 'Reorder Qty', type: 'number', min: 0,
    help: 'Suggested quantity to reorder.', aliases: ['reorderqty', 'reorderquantity', 'reorderamount', 'orderqty'] },
  { key: 'warehouseId', label: 'Warehouse ID', type: 'string', maxLen: 100,
    help: 'Existing warehouse identifier. Defaults to "default".', aliases: ['warehouseid', 'warehouse', 'location', 'locationid', 'site'] },
  { key: 'tags', label: 'Tags', type: 'list',
    help: 'Semicolon- or pipe-separated list, e.g. "fragile;seasonal".', aliases: ['tags', 'tag', 'labels', 'keywords'] },
  { key: 'serialNumber', label: 'Serial Number', type: 'string', maxLen: 200, feature: 'inventory.serial_number_tracking',
    help: 'Requires Serial Number Tracking.', aliases: ['serialnumber', 'serial', 'serialno', 'sn'] },
  { key: 'lotNumber', label: 'Lot / Batch Number', type: 'string', maxLen: 200, feature: 'inventory.lot_number_tracking',
    help: 'Requires Lot/Batch Tracking.', aliases: ['lotnumber', 'lot', 'batch', 'batchnumber', 'lotno', 'batchno'] },
  { key: 'expiryDate', label: 'Expiry Date', type: 'date', feature: 'inventory.expiry_date_tracking',
    help: 'Requires Expiry Date Tracking. Use YYYY-MM-DD.', aliases: ['expirydate', 'expiry', 'expiration', 'expirationdate', 'bestbefore', 'useby', 'expires'] },
  { key: 'vendorName', label: 'Vendor Name', type: 'string', maxLen: 200, feature: 'inventory.lot_number_tracking',
    help: 'Requires Lot/Batch Tracking.', aliases: ['vendorname', 'vendor', 'supplier', 'suppliername', 'manufacturer'] },
  { key: 'vendorId', label: 'Vendor ID', type: 'string', maxLen: 100, feature: 'inventory.lot_number_tracking',
    help: 'Requires Lot/Batch Tracking.', aliases: ['vendorid', 'supplierid', 'vendorcode', 'suppliercode'] },
  { key: 'vendorContact', label: 'Vendor Contact', type: 'string', maxLen: 200, feature: 'inventory.lot_number_tracking',
    help: 'Requires Lot/Batch Tracking.', aliases: ['vendorcontact', 'suppliercontact', 'contact', 'contactname'] },
  { key: 'vendorPhone', label: 'Vendor Phone', type: 'string', maxLen: 50, feature: 'inventory.lot_number_tracking',
    help: 'Requires Lot/Batch Tracking.', aliases: ['vendorphone', 'supplierphone', 'phone', 'telephone', 'tel'] },
  { key: 'vendorAddress', label: 'Vendor Address', type: 'string', maxLen: 500, feature: 'inventory.lot_number_tracking',
    help: 'Requires Lot/Batch Tracking.', aliases: ['vendoraddress', 'supplieraddress', 'address'] },
];

const IGNORE = '__ignore__';

// ─── CSV parsing ─────────────────────────────────────────────────────────────

/**
 * RFC 4180 CSV parser: handles quoted fields containing the delimiter, escaped
 * double quotes (""), embedded newlines, CRLF line endings, and a leading BOM.
 * Returns an array of string arrays.
 */
export function parseCsv(text, delimiter = ',') {
  const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  let i = 0;

  while (i < src.length) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') { field += '"'; i += 2; continue; }
        inQuotes = false; i += 1; continue;
      }
      field += ch; i += 1; continue;
    }
    if (ch === '"') { inQuotes = true; i += 1; continue; }
    if (ch === delimiter) { row.push(field); field = ''; i += 1; continue; }
    if (ch === '\r') { i += 1; continue; }
    if (ch === '\n') { row.push(field); rows.push(row); row = []; field = ''; i += 1; continue; }
    field += ch; i += 1;
  }
  // Trailing field/row (file may not end with a newline).
  if (field !== '' || row.length > 0) { row.push(field); rows.push(row); }
  // Drop fully-blank rows (trailing newlines, spacer lines).
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
}

/** Guess the delimiter from the header line — comma, semicolon, or tab. */
function detectDelimiter(text) {
  const firstLine = (text.split(/\r?\n/)[0] || '');
  const counts = [',', ';', '\t'].map((d) => ({ d, n: firstLine.split(d).length - 1 }));
  counts.sort((a, b) => b.n - a.n);
  return counts[0].n > 0 ? counts[0].d : ',';
}

const normalizeHeader = (h) => String(h || '').toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * UTF-8 byte length of a string. A 4,000-character description of accented or
 * CJK text is meaningfully larger than 4,000 bytes, and the server's limit is
 * in bytes, so character length would under-count.
 */
function utf8Bytes(str) {
  if (typeof TextEncoder !== 'undefined') { return new TextEncoder().encode(str).length; }
  // Fallback for environments without TextEncoder (jsdom under Jest). Exact,
  // not an estimate: encodeURIComponent emits one %XX per UTF-8 byte.
  return encodeURIComponent(str).replace(/%[0-9A-F]{2}/gi, 'x').length;
}

/**
 * Split payloads into batches bounded by BOTH row count and serialized byte
 * size, so a page of long descriptions can't produce a request the batch
 * endpoint rejects with PAYLOAD_TOO_LARGE. A single row over the byte budget
 * still goes out alone — better a clear server error than silently dropping it.
 */
export function chunkPayloads(payloads, maxRows = BATCH_SIZE, maxBytes = BATCH_MAX_BYTES) {
  const chunks = [];
  let current = [];
  let bytes = 0;
  for (const payload of payloads) {
    const size = utf8Bytes(JSON.stringify(payload)) + 1;
    if (current.length > 0 && (current.length >= maxRows || bytes + size > maxBytes)) {
      chunks.push(current);
      current = [];
      bytes = 0;
    }
    current.push(payload);
    bytes += size;
  }
  if (current.length > 0) { chunks.push(current); }
  return chunks;
}

/** Best-effort CSV header → inventory field mapping. Each field maps at most once. */
function autoMap(headers, fields) {
  const mapping = {};
  const taken = new Set();
  headers.forEach((header, idx) => {
    const norm = normalizeHeader(header);
    if (!norm) { mapping[idx] = IGNORE; return; }
    const match = fields.find((f) => !taken.has(f.key) && (normalizeHeader(f.key) === norm || f.aliases.includes(norm)));
    if (match) { taken.add(match.key); mapping[idx] = match.key; } else { mapping[idx] = IGNORE; }
  });
  return mapping;
}

// ─── Value coercion + validation ─────────────────────────────────────────────

/** Strip currency symbols, thousands separators, and trailing % before parsing. */
function toNumber(raw) {
  const cleaned = String(raw).replace(/[^0-9.-]/g, '');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') { return NaN; }
  return Number(cleaned);
}

/** Accept YYYY-MM-DD, MM/DD/YYYY and DD-MM-YYYY; normalize to YYYY-MM-DD. */
function toIsoDate(raw) {
  const v = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) { return v; }
  const slash = v.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (slash) {
    const [, a, b, y] = slash;
    // Ambiguous D/M vs M/D: treat >12 in the first position as day-first.
    const [month, day] = Number(a) > 12 ? [b, a] : [a, b];
    return `${y}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }
  return null;
}

const FIELD_BY_KEY = FIELDS.reduce((acc, f) => { acc[f.key] = f; return acc; }, {});

/**
 * Turn one raw CSV row into an API payload plus any validation errors.
 * `mapping` is { columnIndex: fieldKey }.
 */
function buildRow(cells, mapping) {
  const item = {};
  const errors = [];

  Object.entries(mapping).forEach(([idxStr, fieldKey]) => {
    if (fieldKey === IGNORE) { return; }
    const field = FIELD_BY_KEY[fieldKey];
    if (!field) { return; }
    const raw = (cells[Number(idxStr)] ?? '').trim();
    if (raw === '') { return; }

    if (field.type === 'number') {
      const n = toNumber(raw);
      if (Number.isNaN(n)) { errors.push(`${field.label}: "${raw}" is not a number.`); return; }
      if (field.min !== undefined && n < field.min) { errors.push(`${field.label} must be >= ${field.min}.`); return; }
      item[fieldKey] = n;
      return;
    }
    if (field.type === 'date') {
      const iso = toIsoDate(raw);
      if (!iso) { errors.push(`${field.label}: "${raw}" is not a valid date (use YYYY-MM-DD).`); return; }
      item[fieldKey] = iso;
      return;
    }
    if (field.type === 'list') {
      item[fieldKey] = raw.split(/[;|]/).map((t) => t.trim()).filter(Boolean);
      return;
    }
    if (field.maxLen && raw.length > field.maxLen) {
      errors.push(`${field.label} exceeds ${field.maxLen} characters.`);
      return;
    }
    item[fieldKey] = raw;
  });

  FIELDS.filter((f) => f.required).forEach((f) => {
    if (!item[f.key]) { errors.push(`${f.label} is required.`); }
  });

  return { item, errors };
}

// ─── Sample template ─────────────────────────────────────────────────────────

function downloadSample(fields) {
  const headers = fields.map((f) => f.label);
  const sample = fields.map((f) => {
    switch (f.key) {
      case 'name': return 'Blue Widget';
      case 'sku': return 'WID-BLUE-001';
      case 'quantity': return '120';
      case 'unit': return 'piece';
      case 'category': return 'Widgets';
      case 'description': return 'Standard blue widget, 10mm';
      case 'costPrice': return '4.25';
      case 'salePrice': return '9.99';
      case 'barcode': return '012345678905';
      case 'reorderPoint': return '25';
      case 'reorderQty': return '100';
      case 'warehouseId': return 'default';
      case 'tags': return 'fragile;seasonal';
      case 'expiryDate': return '2027-01-31';
      case 'lotNumber': return 'LOT-2026-A';
      case 'serialNumber': return 'SN-000123';
      case 'vendorName': return 'Acme Supply Co';
      default: return '';
    }
  });
  const esc = (v) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const csv = `${headers.map(esc).join(',')}\n${sample.map(esc).join(',')}\n`;
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'inventory-import-template.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function ImportCsvDialog({ open, onClose, onImported, features = {} }) {
  const [step, setStep] = useState(0);
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [dupeMode, setDupeMode] = useState('update'); // update | skip | create
  const [existingBySku, setExistingBySku] = useState({});
  const [lookupTruncated, setLookupTruncated] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  // Only offer fields the tenant's plan actually supports.
  const availableFields = useMemo(
    () => FIELDS.filter((f) => !f.feature || features[f.feature]),
    [features]
  );

  const reset = useCallback(() => {
    setStep(0); setFileName(''); setHeaders([]); setRows([]); setMapping({});
    setDupeMode('update'); setExistingBySku({}); setLookupTruncated(false);
    setParseError(null); setImporting(false); setProgress(0); setResult(null);
  }, []);

  const handleClose = () => {
    if (importing) { return; }
    const didImport = Boolean(result && result.created + result.updated > 0);
    reset();
    onClose(didImport);
  };

  // Page through existing items so SKU matches are checked against the whole
  // catalogue, not just the page currently rendered behind the dialog.
  const loadExisting = useCallback(async () => {
    const bySku = {};
    let cursor = null;
    let truncated = false;
    for (let page = 0; page < LOOKUP_MAX_PAGES; page += 1) {
      const params = { limit: LOOKUP_PAGE_SIZE, showInactive: 'true', showDeleted: 'true' };
      if (cursor) { params.cursor = cursor; }
      // Sequential by necessity — each page's cursor comes from the previous one.
      // A lookup failure shouldn't block the import; fall back to create-only.
      const data = await tenantApi.listInventory(params).catch(() => null);
      if (!data) { break; }
      (data.items || []).forEach((it) => {
        if (it.sku) { bySku[String(it.sku).toLowerCase()] = it; }
      });
      cursor = data.cursor || null;
      if (!cursor) { break; }
      if (page === LOOKUP_MAX_PAGES - 1) { truncated = true; }
    }
    setExistingBySku(bySku);
    setLookupTruncated(truncated);
  }, []);

  const handleFile = async (file) => {
    if (!file) { return; }
    setParseError(null);
    if (!/\.(csv|txt)$/i.test(file.name)) {
      setParseError('Please choose a .csv file.');
      return;
    }
    let text;
    try {
      text = await file.text();
    } catch {
      setParseError('Could not read that file.');
      return;
    }
    const parsed = parseCsv(text, detectDelimiter(text));
    if (parsed.length < 2) {
      setParseError('The file needs a header row plus at least one data row.');
      return;
    }
    const [headerRow, ...dataRows] = parsed;
    if (dataRows.length > MAX_ROWS) {
      setParseError(`That file has ${dataRows.length.toLocaleString()} rows. The limit is ${MAX_ROWS.toLocaleString()} per import — split the file and import in parts.`);
      return;
    }
    setFileName(file.name);
    setHeaders(headerRow);
    setRows(dataRows);
    setMapping(autoMap(headerRow, availableFields));
    setStep(1);
    loadExisting();
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  // Every field mapped to at least one column, and no field mapped twice.
  const mappedKeys = useMemo(() => Object.values(mapping).filter((v) => v !== IGNORE), [mapping]);
  const duplicateMappings = useMemo(
    () => [...new Set(mappedKeys.filter((k, i) => mappedKeys.indexOf(k) !== i))],
    [mappedKeys]
  );
  const missingRequired = useMemo(
    () => FIELDS.filter((f) => f.required && !mappedKeys.includes(f.key)).map((f) => f.label),
    [mappedKeys]
  );
  const mappingValid = missingRequired.length === 0 && duplicateMappings.length === 0;

  // Validate + classify every row. Recomputed when mapping or dupe mode changes.
  const analysis = useMemo(() => {
    if (!mappingValid || rows.length === 0) { return null; }
    const seenSkus = new Map(); // sku (lowercase) → first row number in this file
    const analyzed = rows.map((cells, idx) => {
      const { item, errors } = buildRow(cells, mapping);
      const rowNum = idx + 2; // +1 for header, +1 for 1-based display
      const skuKey = item.sku ? String(item.sku).toLowerCase() : null;

      if (skuKey) {
        if (seenSkus.has(skuKey)) {
          errors.push(`Duplicate SKU within this file (also on row ${seenSkus.get(skuKey)}).`);
        } else {
          seenSkus.set(skuKey, rowNum);
        }
      }

      const existing = skuKey ? existingBySku[skuKey] : null;
      let action = 'create';
      if (errors.length > 0) {
        action = 'error';
      } else if (existing) {
        if (existing.type === 'bundle') {
          // The batch endpoint refuses to overwrite a bundle; flag it here so
          // the user sees why rather than getting a server-side failure.
          action = 'error';
          errors.push('A bundle already uses this SKU. Bundles cannot be imported or overwritten.');
        } else if (dupeMode === 'update') {
          action = 'update';
        } else if (dupeMode === 'skip') {
          action = 'skip';
        }
      }
      const payload = action === 'update' ? { ...item, itemId: existing.itemId } : item;
      return { rowNum, action, errors, payload, existing };
    });

    return {
      rows: analyzed,
      create: analyzed.filter((r) => r.action === 'create').length,
      update: analyzed.filter((r) => r.action === 'update').length,
      skip: analyzed.filter((r) => r.action === 'skip').length,
      error: analyzed.filter((r) => r.action === 'error').length,
    };
  }, [rows, mapping, mappingValid, existingBySku, dupeMode]);

  const importable = analysis ? analysis.rows.filter((r) => r.action === 'create' || r.action === 'update') : [];

  const runImport = async () => {
    setImporting(true);
    setProgress(0);
    setStep(3);
    const payloads = importable.map((r) => r.payload);
    const summary = { created: 0, updated: 0, failed: 0, errors: [] };
    const createdCount = importable.filter((r) => r.action === 'create').length;

    // Chunks are sent one at a time so progress is meaningful and the tenant's
    // API rate limit isn't hit by a burst of parallel batch writes.
    const chunks = chunkPayloads(payloads);
    let sent = 0;
    for (const chunk of chunks) {
      try {
        const resp = await tenantApi.batchUpsertInventory(chunk);
        summary.created += resp.created ?? 0;
        summary.updated += resp.updated ?? 0;
        summary.failed += resp.failed ?? (resp.errors?.length || 0);
        (resp.errors || []).forEach((e) => {
          summary.errors.push({ sku: e.sku, message: e.error || e.message || 'Unknown error' });
        });
      } catch (e) {
        summary.failed += chunk.length;
        summary.errors.push({ sku: null, message: `Batch of ${chunk.length} rows failed: ${e.message}` });
      }
      sent += chunk.length;
      setProgress(Math.round((sent / payloads.length) * 100));
    }

    // Older backends returned only `processed`; fall back to the client's own
    // create/update split so the summary is never blank.
    if (summary.created === 0 && summary.updated === 0 && summary.failed === 0) {
      summary.created = createdCount;
      summary.updated = payloads.length - createdCount;
    }
    summary.skipped = analysis ? analysis.skip : 0;
    setResult(summary);
    setImporting(false);
    if (summary.created + summary.updated > 0) { onImported?.(); }
  };

  // ─── Step renderers ────────────────────────────────────────────────────────

  const renderUpload = () => (
    <Stack spacing={2}>
      <Paper
        variant="outlined"
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        sx={{
          p: 4, textAlign: 'center', cursor: 'pointer', borderStyle: 'dashed', borderWidth: 2,
          borderColor: dragActive ? 'primary.main' : 'divider',
          bgcolor: dragActive ? 'action.hover' : 'transparent',
          transition: 'border-color .15s, background-color .15s',
        }}
      >
        <UploadFileIcon color="primary" sx={{ fontSize: 44, mb: 1 }} />
        <Typography variant="subtitle1" fontWeight={600}>Drop a CSV file here, or click to browse</Typography>
        <Typography variant="body2" color="text.secondary">
          Up to {MAX_ROWS.toLocaleString()} rows · .csv · comma, semicolon, or tab separated
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          hidden
          onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
        />
      </Paper>

      {parseError && <Alert severity="error">{parseError}</Alert>}

      <Alert severity="info" icon={false}>
        <AlertTitle sx={{ fontWeight: 700 }}>How the file should look</AlertTitle>
        <Typography variant="body2" sx={{ mb: 1 }}>
          The first row must be a header row naming each column. Column order does not matter —
          you will map each column to an inventory field on the next step. Values containing commas
          should be wrapped in double quotes.
        </Typography>
        <Box component="pre" sx={{
          m: 0, p: 1.5, borderRadius: 1, bgcolor: 'action.hover', overflowX: 'auto',
          fontSize: 12, fontFamily: 'monospace',
        }}>
{`Name,SKU,Quantity,Unit,Category,Cost Price,Sale Price
Blue Widget,WID-BLUE-001,120,piece,Widgets,4.25,9.99
"Bracket, heavy duty",BRK-HD-002,40,box,Hardware,12.00,24.50`}
        </Box>
        <Button size="small" startIcon={<DownloadIcon />} sx={{ mt: 1.5 }}
          onClick={(e) => { e.stopPropagation(); downloadSample(availableFields); }}>
          Download template CSV
        </Button>
      </Alert>

      <Box>
        <Typography variant="subtitle2" gutterBottom>Supported fields</Typography>
        <Paper variant="outlined" sx={{ maxHeight: 240, overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '28%' }}>Field</TableCell>
                <TableCell sx={{ width: '14%' }}>Required</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {availableFields.map((f) => (
                <TableRow key={f.key}>
                  <TableCell>{f.label}</TableCell>
                  <TableCell>
                    {f.required
                      ? <Chip size="small" color="error" label="Required" />
                      : <Typography variant="caption" color="text.secondary">Optional</Typography>}
                  </TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{f.help}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
        {FIELDS.some((f) => f.feature && !features[f.feature]) && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Serial number, lot/batch, expiry date, and vendor columns appear here once the matching
            add-on is enabled on the Features &amp; Add-Ons page. Columns for disabled fields are
            ignored on import.
          </Typography>
        )}
      </Box>

      <Alert severity="warning">
        Only standard inventory items can be imported. Bundles have stock computed from their linked
        components, so they must be created individually from the Add dialog.
      </Alert>
    </Stack>
  );

  const renderMapping = () => (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip size="small" label={fileName} />
        <Typography variant="body2" color="text.secondary">
          {rows.length.toLocaleString()} data rows · {headers.length} columns
        </Typography>
      </Stack>

      {missingRequired.length > 0 && (
        <Alert severity="error">Map a column to: <strong>{missingRequired.join(', ')}</strong>.</Alert>
      )}
      {duplicateMappings.length > 0 && (
        <Alert severity="error">
          Each field can only be mapped once. Duplicated:{' '}
          <strong>{duplicateMappings.map((k) => FIELD_BY_KEY[k]?.label || k).join(', ')}</strong>.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ maxHeight: 320, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '30%' }}>CSV column</TableCell>
              <TableCell sx={{ width: '30%' }}>Sample value</TableCell>
              <TableCell sx={{ width: '40%' }}>Inventory field</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {headers.map((header, idx) => (
              <TableRow key={`${header}-${idx}`}>
                <TableCell><Typography variant="body2" fontWeight={600}>{header || <em>(unnamed)</em>}</Typography></TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 200 }}>
                    {rows[0]?.[idx] || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <TextField
                    select fullWidth size="small"
                    value={mapping[idx] ?? IGNORE}
                    onChange={(e) => setMapping((m) => ({ ...m, [idx]: e.target.value }))}
                  >
                    <MenuItem value={IGNORE}><em>Do not import</em></MenuItem>
                    {availableFields.map((f) => (
                      <MenuItem key={f.key} value={f.key}>
                        {f.label}{f.required ? ' *' : ''}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Divider />

      <FormControl>
        <FormLabel sx={{ fontSize: 14, fontWeight: 600 }}>When a row&apos;s SKU already exists</FormLabel>
        <RadioGroup value={dupeMode} onChange={(e) => setDupeMode(e.target.value)}>
          <FormControlLabel value="update" control={<Radio size="small" />}
            label={<Typography variant="body2">Update the existing item — mapped columns overwrite current values, unmapped fields are left alone</Typography>} />
          <FormControlLabel value="skip" control={<Radio size="small" />}
            label={<Typography variant="body2">Skip the row — leave the existing item untouched</Typography>} />
          <FormControlLabel value="create" control={<Radio size="small" />}
            label={<Typography variant="body2">Create a second item anyway — allows duplicate SKUs</Typography>} />
        </RadioGroup>
      </FormControl>

      {lookupTruncated && (
        <Alert severity="warning">
          You have more items than can be checked in one pass, so some existing SKUs may not be
          detected as duplicates.
        </Alert>
      )}
    </Stack>
  );

  const renderReview = () => (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip color="success" label={`${analysis?.create ?? 0} new`} />
        <Chip color="info" label={`${analysis?.update ?? 0} to update`} />
        <Chip label={`${analysis?.skip ?? 0} skipped`} />
        <Chip color="error" variant={analysis?.error ? 'filled' : 'outlined'} label={`${analysis?.error ?? 0} with errors`} />
      </Stack>

      {analysis?.error > 0 && (
        <Alert severity="warning">
          Rows with errors are not imported. Fix them in your file and re-upload, or continue to
          import the {importable.length.toLocaleString()} valid row{importable.length === 1 ? '' : 's'}.
        </Alert>
      )}
      {importable.length === 0 && (
        <Alert severity="error">Nothing to import — every row is either skipped or has an error.</Alert>
      )}

      <Paper variant="outlined" sx={{ maxHeight: 360, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 60 }}>Row</TableCell>
              <TableCell sx={{ width: 90 }}>Action</TableCell>
              <TableCell sx={{ width: '22%' }}>Name</TableCell>
              <TableCell sx={{ width: '18%' }}>SKU</TableCell>
              <TableCell align="right" sx={{ width: 70 }}>Qty</TableCell>
              <TableCell>Issues</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(analysis?.rows || []).slice(0, 200).map((r) => {
              const chip = ACTION_CHIP[r.action];
              return (
                <TableRow
                  key={r.rowNum}
                  sx={r.action === 'error'
                    ? { bgcolor: (t) => alpha(t.palette.error.main, 0.08) }
                    : undefined}
                >
                  <TableCell><Typography variant="caption" color="text.secondary">{r.rowNum}</Typography></TableCell>
                  <TableCell><Chip size="small" color={chip.color} variant={r.action === 'skip' ? 'outlined' : 'filled'} label={chip.label} /></TableCell>
                  <TableCell><Typography variant="body2" noWrap>{r.payload.name || '—'}</Typography></TableCell>
                  <TableCell><Typography variant="body2" noWrap>{r.payload.sku || '—'}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="body2">{r.payload.quantity ?? 0}</Typography></TableCell>
                  <TableCell>
                    {r.errors.length > 0
                      ? <Typography variant="caption" color="error">{r.errors.join(' ')}</Typography>
                      : <Typography variant="caption" color="text.secondary">—</Typography>}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
      {(analysis?.rows.length || 0) > 200 && (
        <Typography variant="caption" color="text.secondary">
          Showing the first 200 of {analysis.rows.length.toLocaleString()} rows. All rows are imported.
        </Typography>
      )}
    </Stack>
  );

  const renderImport = () => (
    <Stack spacing={2}>
      {importing && (
        <>
          <Typography variant="body2">Importing {importable.length.toLocaleString()} rows…</Typography>
          <LinearProgress variant="determinate" value={progress} />
          <Typography variant="caption" color="text.secondary">{progress}% complete — keep this dialog open.</Typography>
        </>
      )}
      {!importing && result && (
        <>
          <Stack direction="row" spacing={1} alignItems="center">
            {result.failed > 0
              ? <ErrorOutlineIcon color="warning" />
              : <CheckCircleIcon color="success" />}
            <Typography variant="h6">
              {result.failed > 0 ? 'Import finished with errors' : 'Import complete'}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip color="success" label={`${result.created} created`} />
            <Chip color="info" label={`${result.updated} updated`} />
            <Chip label={`${result.skipped || 0} skipped`} />
            {result.failed > 0 && <Chip color="error" label={`${result.failed} failed`} />}
          </Stack>
          {result.errors.length > 0 && (
            <Paper variant="outlined" sx={{ maxHeight: 240, overflow: 'auto', p: 1.5 }}>
              {result.errors.map((e, i) => (
                <Typography key={i} variant="caption" color="error" display="block">
                  {e.sku ? `${e.sku}: ` : ''}{e.message}
                </Typography>
              ))}
            </Paper>
          )}
        </>
      )}
    </Stack>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Import Inventory from CSV
        <Tooltip title="Close">
          <span>
            <IconButton onClick={handleClose} disabled={importing} sx={{ position: 'absolute', right: 12, top: 12 }}>
              <CloseIcon />
            </IconButton>
          </span>
        </Tooltip>
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {STEPS.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
        </Stepper>
        {step === 0 && renderUpload()}
        {step === 1 && renderMapping()}
        {step === 2 && renderReview()}
        {step === 3 && renderImport()}
      </DialogContent>
      <DialogActions>
        {step === 1 && <Button onClick={() => { reset(); }}>Choose another file</Button>}
        {step === 2 && <Button onClick={() => setStep(1)}>Back to mapping</Button>}
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleClose} disabled={importing}>
          {result ? 'Done' : 'Cancel'}
        </Button>
        {step === 1 && (
          <Button variant="contained" disabled={!mappingValid} onClick={() => setStep(2)}>
            Review {rows.length.toLocaleString()} rows
          </Button>
        )}
        {step === 2 && (
          <Button variant="contained" disabled={importable.length === 0} onClick={runImport}>
            Import {importable.length.toLocaleString()} row{importable.length === 1 ? '' : 's'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
